import axios from 'axios'
import { useSentry } from '~/composables/useSentry'

const GEO_BLOCK_CACHE_KEY = 'sharesquare.geo-blocked'
const REGION_BLOCKED_PATH = '/region-blocked'

const isRegionBlockedResponse = (response, forbiddenMessage) =>
  response?.status === 403 && response?.data?.message === forbiddenMessage

const writeGeoBlockCache = () => {
  if (typeof sessionStorage === 'undefined') {
    return
  }

  try {
    sessionStorage.setItem(GEO_BLOCK_CACHE_KEY, 'true')
  } catch (error) {
    console.warn('Unable to cache geo-block status:', error)
  }
}

const clearAuthSession = (nuxtApp) => {
  nuxtApp.$auth.clearAuthData()
  nuxtApp.$auth.clearCookie()
}

export default defineNuxtPlugin((nuxtApp) => {
  const api = axios.create({
    baseURL: nuxtApp.$config.public.baseSharesquareApi,
    withCredentials: true,
    headers: {
      common: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      'Content-Type': 'application/json',
    },
  })

  api.interceptors.request.use((config) => {
    const excludedEndpoints = ['strings', 'auth/login']

    const tenantName = nuxtApp.$auth.getTenantName()
    if (!tenantName) {
      console.warn('No tenant name found in localStorage')
      return config
    }

    if (
      !excludedEndpoints.some((endpoint) => config.url?.includes(endpoint)) &&
      !config.url?.includes('static/')
    ) {
      config.url = config.url.startsWith('/')
        ? `${tenantName}${config.url}`
        : `${tenantName}/${config.url}`
    }

    return config
  })

  api.interceptors.response.use(undefined, async function (error) {
    if (process.client) {
      const requestConfig = error.config || {}
      const response = error.response
      const skipSentryCapture = [401, 403, 404]

      if (!skipSentryCapture.includes(response?.status)) {
        const { captureApiError } = useSentry()
        captureApiError(error, requestConfig, response)
      }
    }

    if (error.response) {
      if (
        isRegionBlockedResponse(
          error.response,
          nuxtApp.$config.public.geofenceForbiddenMessage
        )
      ) {
        writeGeoBlockCache()
        clearAuthSession(nuxtApp)
        await nuxtApp.$router.push(REGION_BLOCKED_PATH)
        throw new Error('REGION_BLOCKED_REDIRECT')
      }

      if (error.code == 'ERR_NETWORK' || error.response.status == 503) {
        clearAuthSession(nuxtApp)
        await nuxtApp.$router.push('/maintenance')
        throw new Error('MAINTENANCE_MODE_REDIRECT')
      }

      if (error.response.status === 401) {
        nuxtApp.$toast.error('session expired please login again')
        clearAuthSession(nuxtApp)
      }
    } else if (error.request) {
      console.error('Request error:', error.request)
    } else {
      console.error('Error:', error.message)
    }

    return Promise.reject(error)
  })

  return {
    provide: {
      axios: api,
    },
  }
})